import { useToken } from '@ori/auth';
import { getHeaders, useGraphqlQuery } from '@ori/fetching';
import cloneDeep from 'lodash.clonedeep';
import { useMemo } from 'react';

import { PACKAGE_NAME } from '../../constants';
import useSponsorPersonalBeautyStoreInfo from '../../hooks/useSponsorPersonalBeautyStoreInfo';
import type { ProductListType } from '../../models';
import { ReferralType } from '../../models';
import type { BeautyStoreQuery, BeautyStoreQueryVariables } from '../../models/graphql-types/operations';
import { beautyStoreQuery } from '../../queries';
import { useConfiguration } from '../ConfigurationProvider';
import { useCookieStoreInfo } from './useCookieStoreInfo';

export const usePersonalBeautyStoreData = () => {
  const { isEnabled, currentLanguage, graphqlUrl, tenant } = useConfiguration();
  const { isAnonymousUser, customerId, token } = useToken();
  const { type, owner, productListType } = useCookieStoreInfo();
  const { data: sponsorData } = useSponsorPersonalBeautyStoreInfo();
  const shouldCall = type === ReferralType.PBS && isEnabled && Boolean(owner);
  const { data: dataFromApi, error } = useGraphqlQuery<BeautyStoreQuery, BeautyStoreQueryVariables>({
    url: graphqlUrl,
    query: beautyStoreQuery,
    headers: getHeaders({
      tenant,
      language: currentLanguage,
      customerId,
      token,
      sourceAppName: PACKAGE_NAME,
    }),
    variables: {
      name: isAnonymousUser ? owner : sponsorData?.name,
      productListType: productListType as ProductListType,
    },
    skip: isAnonymousUser ? !shouldCall : !sponsorData?.name,
  });

  if (error) {
    // eslint-disable-next-line no-console -- We need to log the error
    console.error(error);
  }

  return useMemo(() => {
    if (!dataFromApi?.beautyStore || !dataFromApi.application) {
      return null;
    }

    const result = cloneDeep(dataFromApi);

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line unicorn/no-lonely-if -- webpack dead code elimination
      if (result.beautyStore?.profile?.photoUrl) {
        const photoUrl = new URL(result.beautyStore.profile.photoUrl);

        // photoUrl is readOnly so it cannot be reassigned
        return {
          ...result,
          beautyStore: {
            ...result.beautyStore,
            profile: {
              ...result.beautyStore.profile,
              photoUrl: `${photoUrl.pathname}?${photoUrl.searchParams.toString()}`,
            },
          },
        };
      }
    }

    return result;
  }, [dataFromApi]);
};
